import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Search, Edit, Copy, Trash2, Upload, Sparkles, Filter, Tag, X } from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import toast from 'react-hot-toast';
import { getTemplates, deleteTemplate, duplicateTemplate, getTemplate, updateTemplate, getTemplateTags, updateTemplateTags, uploadDocxTemplate } from '../../api/documentTemplates';
import client from '../../api/client';

export default function TemplateLibrary() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [recordTypes, setRecordTypes] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadTemplates();
    loadRecordTypes();
  }, [filterType]);

  const loadTemplates = async () => {
    try {
      const params = {};
      if (filterType) params.record_type_id = filterType;
      const { data } = await getTemplates(params);
      setTemplates(data.map(t => ({ ...t, tags: typeof t.tags === 'string' ? JSON.parse(t.tags) : (t.tags || []) })));
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadRecordTypes = async () => {
    try {
      const { data } = await client.get("/admin/record-types"); const list = data.record_types || data;
      setRecordTypes(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load record types:', err);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await deleteTemplate(id);
      toast.success('Template deleted');
      loadTemplates();
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await duplicateTemplate(id);
      toast.success('Template duplicated');
      loadTemplates();
    } catch (err) {
      toast.error('Failed to duplicate template');
    }
  };

  const handleTagsChanged = (templateId, newTags) => {
    setTemplates(prev => prev.map(t =>
      t.id === templateId ? { ...t, tags: newTags } : t
    ));
  };

  const filtered = templates.filter(t => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    if (t.name.toLowerCase().includes(q)) return true;
    if (t.description?.toLowerCase().includes(q)) return true;
    if (t.tags && t.tags.some(tag => tag.name.toLowerCase().includes(q))) return true;
    return false;
  });

  const categoryColors = {
    general: 'bg-gray-100 text-gray-700',
    letter: 'bg-blue-100 text-blue-700',
    legal: 'bg-purple-100 text-purple-700',
    intake: 'bg-green-100 text-green-700',
    billing: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <AdminPageHeader title="Document Templates" description="Create and manage document templates with auto-fill placeholders" icon={FileText} iconColor="text-cyan-600" iconBg="bg-cyan-50">
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="w-4 h-4" />
          New Template
        </button>
      </AdminPageHeader>

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="pl-10 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
          >
            <option value="">All Record Types</option>
            {recordTypes.map(rt => (
              <option key={rt.id} value={rt.id}>{rt.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tag filter pills */}
      {(() => {
        const allTags = [];
        const seen = new Set();
        templates.forEach(t => (t.tags || []).forEach(tag => {
          const key = tag.name.toLowerCase();
          if (!seen.has(key)) { seen.add(key); allTags.push(tag); }
        }));
        if (allTags.length === 0) return null;
        return (
          <div className="flex flex-wrap gap-2 mb-5">
            <span className="text-xs text-gray-400 py-1">Tags:</span>
            {allTags.map(tag => {
              const isActive = search.toLowerCase() === tag.name.toLowerCase();
              return (
                <button key={tag.id} onClick={() => setSearch(isActive ? '' : tag.name)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border transition ${
                    isActive
                      ? 'bg-blue-100 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: tag.color }} />
                  {tag.name}
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* Template Grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading templates...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No templates yet</p>
          <p className="text-gray-400 text-sm mt-1">Create your first template to start generating documents</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Create Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(template => (
            <div
              key={template.id}
              className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition cursor-pointer group"
              onClick={() => navigate(`/admin/doc-templates/${template.id}/edit`)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <h3 className="font-semibold text-gray-900 truncate">{template.name}</h3>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/admin/doc-templates/${template.id}/edit`, template.template_type === 'docx' ? {} : { state: { mode: 'import' } }); }}
                    className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                    title="Edit with AI Import"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDuplicate(template.id); }}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                    title="Duplicate"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(template.id, template.name); }}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {template.description && (
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">{template.description}</p>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full">
                  {template.record_type_name}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${categoryColors[template.category] || categoryColors.general}`}>
                  {template.category}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  template.template_type === 'docx'
                    ? 'bg-teal-50 text-teal-700'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {template.template_type === 'docx' ? 'DOCX' : 'HTML'}
                </span>
                {template.placeholders?.length > 0 && (
                  <span className="text-xs text-gray-400">
                    {template.placeholders.length} fields
                  </span>
                )}
              </div>
              <InlineTagPicker
                templateId={template.id}
                tags={template.tags || []}
                onTagsChanged={handleTagsChanged}
              />

              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
                <span>by {template.created_by_name || 'Unknown'}</span>
                <span>{new Date(template.updated_at || template.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateTemplateModal
          recordTypes={recordTypes}
          onClose={() => setShowCreateModal(false)}
          onCreated={(id, mode) => {
            setShowCreateModal(false);
            navigate(`/admin/doc-templates/${id}/edit`, { state: { mode } });
          }}
          navigate={navigate}
        />
      )}

    </div>
  );
}

function CreateTemplateModal({ recordTypes, onClose, navigate }) {
  const [mode, setMode] = useState(null); // 'scratch' | 'import' | 'docx'
  const [name, setName] = useState('');
  const [recordTypeId, setRecordTypeId] = useState('');
  const [description, setDescription] = useState('');
  const [docxFile, setDocxFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleCreate = async () => {
    if (!name.trim() || !recordTypeId) {
      toast.error('Name and record type are required');
      return;
    }

    if (mode === 'docx') {
      if (!docxFile) {
        toast.error('Please select a .docx file');
        return;
      }
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', docxFile);
        formData.append('name', name);
        formData.append('record_type_id', recordTypeId);
        formData.append('description', description);
        const { data } = await uploadDocxTemplate(formData);
        toast.success('Word template uploaded!');
        navigate(`/admin/doc-templates/${data.id}/edit`);
      } catch (err) {
        toast.error('Upload failed: ' + (err.response?.data?.error || err.message));
      } finally {
        setUploading(false);
      }
      return;
    }

    navigate(`/admin/doc-templates/new`, {
      state: {
        mode: mode || 'scratch',
        name,
        record_type_id: recordTypeId,
        description
      }
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-4">New Template</h2>

        {/* Step 1: Choose mode */}
        {!mode && (
          <div className="space-y-3">
            <button
              onClick={() => setMode('import')}
              className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition text-left"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Import with AI</p>
                <p className="text-sm text-gray-500">Paste a letter or upload .docx — AI maps placeholders to fields</p>
              </div>
            </button>

            <button
              onClick={() => setMode('scratch')}
              className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition text-left"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Edit className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Write from scratch</p>
                <p className="text-sm text-gray-500">Use the editor with field sidebar to build your template</p>
              </div>
            </button>

            <button
              onClick={() => setMode('docx')}
              className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-teal-500 hover:bg-teal-50 transition text-left"
            >
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <Upload className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Upload Word Document</p>
                <p className="text-sm text-gray-500">Upload a .docx with {'{{placeholders}}'} — merge with record data at generation</p>
              </div>
            </button>

            <button onClick={onClose} className="w-full py-2 text-gray-500 text-sm hover:text-gray-700">Cancel</button>
          </div>
        )}

        {/* Step 2: Name + Record Type + (optional file) */}
        {mode && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Welcome Letter, Demand Letter"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Record Type</label>
              <select
                value={recordTypeId}
                onChange={(e) => setRecordTypeId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select record type...</option>
                {recordTypes.map(rt => (
                  <option key={rt.id} value={rt.id}>{rt.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this template"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {mode === 'docx' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Word Document (.docx)</label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-teal-400 transition">
                  <input
                    type="file"
                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => setDocxFile(e.target.files[0])}
                    className="hidden"
                    id="docx-upload"
                  />
                  <label htmlFor="docx-upload" className="cursor-pointer">
                    {docxFile ? (
                      <div className="flex items-center justify-center gap-2 text-teal-700">
                        <FileText className="w-5 h-5" />
                        <span className="font-medium">{docxFile.name}</span>
                        <span className="text-xs text-gray-400">({(docxFile.size / 1024).toFixed(0)} KB)</span>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Click to select a .docx file</p>
                        <p className="text-xs text-gray-400 mt-1">Use {'{{field_name}}'} placeholders in your document</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => { setMode(null); setDocxFile(null); }}
                className="flex-1 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleCreate}
                disabled={!name.trim() || !recordTypeId || (mode === 'docx' && !docxFile) || uploading}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : mode === 'import' ? 'Continue to Import' : mode === 'docx' ? 'Upload & Create' : 'Open Editor'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function InlineTagPicker({ templateId, tags, onTagsChanged }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const tagNames = tags.map(t => t.name);

  const fetchSuggestions = async (q) => {
    try {
      const { data } = await getTemplateTags(q || '');
      setSuggestions(data.filter(t => !tagNames.some(n => n.toLowerCase() === t.name.toLowerCase())));
    } catch { setSuggestions([]); }
  };

  const saveTags = async (newNames) => {
    setSaving(true);
    try {
      const { data } = await updateTemplateTags(templateId, newNames);
      onTagsChanged(templateId, data);
    } catch {
      toast.error('Failed to update tags');
    } finally {
      setSaving(false);
    }
  };

  const addTag = (name) => {
    const trimmed = name.trim();
    if (!trimmed || tagNames.some(t => t.toLowerCase() === trimmed.toLowerCase())) return;
    const newNames = [...tagNames, trimmed];
    saveTags(newNames);
    setInput('');
    setOpen(false);
  };

  const removeTag = (tagName) => {
    const newNames = tagNames.filter(t => t.toLowerCase() !== tagName.toLowerCase());
    saveTags(newNames);
  };

  return (
    <div className="relative mt-2" ref={ref} onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap items-center gap-1">
        {tags.map(tag => (
          <span key={tag.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full group/tag"
            style={{ background: tag.color + '20', color: tag.color }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tag.color }} />
            {tag.name}
            <button
              onClick={(e) => { e.stopPropagation(); removeTag(tag.name); }}
              className="opacity-0 group-hover/tag:opacity-100 hover:brightness-75 transition-opacity ml-0.5"
              title="Remove tag">
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
            if (!open) fetchSuggestions('');
          }}
          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] rounded-full border border-dashed transition ${
            open ? 'border-blue-300 bg-blue-50 text-blue-600' : 'border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-500'
          }`}
          disabled={saving}
          title="Add tag">
          <Plus className="w-2.5 h-2.5" />
          {tags.length === 0 && <span>Add tag</span>}
        </button>
      </div>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="p-1.5">
            <input
              type="text"
              value={input}
              onChange={(e) => { setInput(e.target.value); fetchSuggestions(e.target.value); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && input.trim()) { e.preventDefault(); addTag(input); }
                if (e.key === 'Escape') setOpen(false);
              }}
              placeholder="Search or create..."
              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div className="max-h-36 overflow-y-auto">
            {suggestions.map(t => (
              <button key={t.id}
                onMouseDown={(e) => { e.preventDefault(); addTag(t.name); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t.color }} />
                {t.name}
              </button>
            ))}
            {input.trim() && !suggestions.some(s => s.name.toLowerCase() === input.trim().toLowerCase()) && (
              <button
                onMouseDown={(e) => { e.preventDefault(); addTag(input); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 text-blue-600 flex items-center gap-1.5 border-t border-gray-100">
                <Plus className="w-3 h-3" /> Create &ldquo;{input.trim()}&rdquo;
              </button>
            )}
            {!input.trim() && suggestions.length === 0 && (
              <p className="px-3 py-2 text-[10px] text-gray-400">No tags yet. Type to create one.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EditTemplateModal({ template, recordTypes, onClose, onSaved, onOpenEditor }) {
  const [name, setName] = useState(template.name || '');
  const [description, setDescription] = useState(template.description || '');
  const [recordTypeId, setRecordTypeId] = useState(template.record_type_id || '');
  const [category, setCategory] = useState(template.category || 'general');
  const [saving, setSaving] = useState(false);

  // Tags
  const [templateTags, setTemplateTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showTagDropdown, setShowTagDropdown] = useState(false);

  useEffect(() => {
    loadFullTemplate();
  }, [template.id]);

  const loadFullTemplate = async () => {
    try {
      const { data } = await getTemplate(template.id);
      setName(data.name || '');
      setDescription(data.description || '');
      setRecordTypeId(data.record_type_id || '');
      setCategory(data.category || 'general');
      setTemplateTags((data.tags || []).map(t => t.name));
    } catch (err) {
      console.error('Failed to load template:', err);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await updateTemplate(template.id, {
        name, description, category, tags: templateTags
      });
      toast.success('Template updated!');
      onSaved();
    } catch (err) {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndEdit = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await updateTemplate(template.id, {
        name, description, category, tags: templateTags
      });
      toast.success('Template updated!');
      onOpenEditor(template.id);
    } catch (err) {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const fetchSuggestions = async (q) => {
    try {
      const { data } = await getTemplateTags(q || '');
      setTagSuggestions(data.filter(t => !templateTags.some(existing => existing.toLowerCase() === t.name.toLowerCase())));
    } catch (err) { setTagSuggestions([]); }
  };

  const addTag = (val) => {
    const trimmed = val.trim();
    if (trimmed && !templateTags.some(t => t.toLowerCase() === trimmed.toLowerCase())) {
      setTemplateTags(prev => [...prev, trimmed]);
    }
    setTagInput('');
    setShowTagDropdown(false);
  };

  const categoryColors = {
    general: 'bg-gray-100 text-gray-700',
    letter: 'bg-blue-100 text-blue-700',
    legal: 'bg-purple-100 text-purple-700',
    intake: 'bg-green-100 text-green-700',
    billing: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Edit className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Edit Template</h2>
              <p className="text-sm text-gray-500">Update properties and tags</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Template Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g., Demand Letter, Welcome Letter"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of this template..."
              rows={2}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none" />
          </div>

          {/* Record Type + Category row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Record Type</label>
              <select value={recordTypeId} onChange={e => setRecordTypeId(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                disabled>
                <option value="">Select...</option>
                {recordTypes.map(rt => (
                  <option key={rt.id} value={rt.id}>{rt.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">Record type cannot be changed after creation</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white">
                <option value="general">General</option>
                <option value="letter">Letter</option>
                <option value="legal">Legal</option>
                <option value="intake">Intake</option>
                <option value="billing">Billing</option>
              </select>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Tags</label>
            <div className="relative">
              <div className="flex items-center gap-1.5 px-3 py-2.5 border border-gray-200 rounded-xl flex-wrap bg-white min-h-[44px]">
                {templateTags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                    {tag}
                    <button onClick={() => setTemplateTags(prev => prev.filter(t => t !== tag))} className="hover:text-blue-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={tagInput}
                  onChange={async (e) => { setTagInput(e.target.value); setShowTagDropdown(true); await fetchSuggestions(e.target.value); }}
                  onFocus={async () => { setShowTagDropdown(true); await fetchSuggestions(''); }}
                  onBlur={() => setTimeout(() => setShowTagDropdown(false), 200)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && tagInput.trim()) { e.preventDefault(); addTag(tagInput); } }}
                  placeholder={templateTags.length === 0 ? "Type to add tags..." : "Add more..."}
                  className="flex-1 min-w-[100px] text-sm bg-transparent border-none focus:outline-none"
                />
                <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </div>
              {showTagDropdown && (tagSuggestions.length > 0 || tagInput.trim()) && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                  {tagSuggestions.map(t => (
                    <button key={t.id} onMouseDown={(e) => { e.preventDefault(); addTag(t.name); }}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: t.color }} />
                      {t.name}
                    </button>
                  ))}
                  {tagInput.trim() && !tagSuggestions.some(s => s.name.toLowerCase() === tagInput.trim().toLowerCase()) && (
                    <button onMouseDown={(e) => { e.preventDefault(); addTag(tagInput); }}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 text-blue-600 flex items-center gap-2 border-t">
                      <Plus className="w-3.5 h-3.5" /> Create \u201c{tagInput.trim()}\u201d
                    </button>
                  )}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1.5">Tags help agents find templates quickly when generating documents</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2.5 text-gray-600 hover:text-gray-800 text-sm">
            Cancel
          </button>
          <div className="flex gap-3">
            <button onClick={handleSave} disabled={saving || !name.trim()}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 text-sm font-medium disabled:opacity-50">
              Save
            </button>
            <button onClick={handleSaveAndEdit} disabled={saving || !name.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50">
              <Edit className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save & Open Editor'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
